import ComHubSkeletonLoader from '@company/components/CommunicationHub/ComHubSkeletonLoader';
import ReviewRequestsTable from '@company/components/CommunicationHub/CommunicationHubTables/ReviewRequestsTable';
import FilterBar from '@company/components/CommunicationHub/FilterBar/FilterBar';
import { clearCompanyIndicatorsCache } from '@company/hooks/useCompanyIndicators';
import {
  dismissItemsFromNew,
  getPendingReviews,
} from '@company/services/com-hub/api';
import {
  ComHubQueryParamsEnum,
  ComHubTabs,
  Review,
  UserActionAssociatedTypeEnum,
  WorkerModalParams,
} from '@company/types/comm-hub.type';
import ThPagination from '@components/ThPagination/ThPagination';
import QueryKeys from '@constants/queryKeys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  dismissItems,
  extractComHubUrlParams,
  handleComHubSearch,
  handleDismissOptimisticRollback,
  handleDismissOptimisticUpdate,
} from '@utils/ComHubUtils';
import { DEFAULT_ITEMS_PER_PAGE } from '@utils/Pagination';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

interface ReviewRequestsProps {
  onShowWorkerProfile?: (data: WorkerModalParams) => void;
  onSeeReviewRequest: (review: Review) => void;
  onShowAlert?: (success: boolean, message: string) => void;
  newRequestsCount?: number;
  onCounterUpdate?: (delta: number) => void;
}

function ReviewRequests({
  onShowWorkerProfile,
  onSeeReviewRequest,
  onShowAlert,
  newRequestsCount,
  onCounterUpdate,
}: ReviewRequestsProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentOffset, setCurrentOffset] = useState(0);
  const [searchState, setSearchState] = useState<{
    searchText: string;
    dateRange: { startDate: Date | undefined; endDate: Date | undefined };
  }>();
  const [showOnlyNew, setShowOnlyNew] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // CONSTANTS FOR QUERY
  const queryClient = useQueryClient();
  const queryKeyReviewRequests = [
    QueryKeys.ComHub.REVIEW_REQUESTS,
    currentOffset,
    searchState,
    showOnlyNew,
  ];

  const {
    data: reviewRequestsResponse,
    isLoading,
    error: reviewRequestsError,
  } = useQuery({
    queryKey: queryKeyReviewRequests,
    queryFn: () =>
      getPendingReviews(
        currentOffset,
        DEFAULT_ITEMS_PER_PAGE,
        searchState?.searchText,
        searchState?.dateRange,
        showOnlyNew
      ),
    enabled: isInitialized, // Only run query after initialization
  });

  // useQuery update
  const { mutateAsync, isPending: isDismissing } = useMutation({
    mutationFn: dismissItemsFromNew,
    onSuccess: (_, { items }) => {
      handleDismissOptimisticUpdate(queryClient, items, 'setNewFalse');
      clearCompanyIndicatorsCache();
    },
    onError: (_, { items }) => {
      handleDismissOptimisticRollback(queryClient, items, 'setNewFalse');
    },
    onSettled: (_, __, { items }) => {
      queryClient.invalidateQueries({ queryKey: queryKeyReviewRequests });
      // Dismiss decreases counter by number of items dismissed
      const dismissedCount = items.reduce(
        (acc, item) => acc + item.associatedIds.length,
        0
      );
      onCounterUpdate?.(-dismissedCount);
    },
  });

  // Handle query errors
  useEffect(() => {
    if (reviewRequestsError) {
      onShowAlert?.(false, reviewRequestsError.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewRequestsError]);

  // Initialize state from URL parameters
  useEffect(() => {
    if (isInitialized) return;

    const {
      searchState: urlSearchState,
      showOnlyNew: urlShowOnlyNew,
      offset: urlOffset,
    } = extractComHubUrlParams(searchParams);

    if (urlSearchState) {
      setSearchState(urlSearchState);
    }

    if (urlShowOnlyNew !== undefined) {
      setShowOnlyNew(urlShowOnlyNew);
    }

    if (urlOffset !== undefined) {
      setCurrentOffset(urlOffset);
    }

    setIsInitialized(true);
  }, [searchParams, isInitialized]);

  const updateSearchParams = useCallback(
    (params: URLSearchParams) => {
      setSearchParams(params);
    },
    [setSearchParams]
  );

  const handleOnSearch = useCallback(
    (search: {
      searchText: string;
      dateRange: { startDate: Date | undefined; endDate: Date | undefined };
    }) => {
      handleComHubSearch(search, setSearchState, updateSearchParams, {
        itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
        setCurrentOffset,
      });
    },
    [updateSearchParams, setCurrentOffset]
  );

  // Update URL when offset changes
  useEffect(() => {
    if (!isInitialized) return;

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set(ComHubQueryParamsEnum.OFFSET, currentOffset.toString());
    newSearchParams.set(
      ComHubQueryParamsEnum.MAX,
      DEFAULT_ITEMS_PER_PAGE.toString()
    );
    updateSearchParams(newSearchParams);
  }, [currentOffset, searchParams, updateSearchParams, isInitialized]);

  return (
    <>
      <FilterBar
        onSearch={handleOnSearch}
        showOnlyNew={showOnlyNew}
        onShowOnlyNew={() => {
          setShowOnlyNew((prevState) => !prevState);
          setCurrentOffset(0);
        }}
      />
      {(isLoading || isDismissing) && <ComHubSkeletonLoader />}
      {!isLoading && !isDismissing && reviewRequestsResponse ? (
        <>
          <ReviewRequestsTable
            title={ComHubTabs.RECEIVED_REQUESTS}
            reviewRequests={reviewRequestsResponse.ratingRequests}
            onSeeReviewRequest={onSeeReviewRequest}
            onShowWorkerProfile={onShowWorkerProfile}
            onDismissItems={(items) =>
              dismissItems(
                items,
                UserActionAssociatedTypeEnum.RATING_REQUEST,
                mutateAsync
              )
            }
            newCount={newRequestsCount}
          />
          <ThPagination
            currentOffset={reviewRequestsResponse.pagination.offset}
            itemsPerPage={DEFAULT_ITEMS_PER_PAGE}
            onPageChange={setCurrentOffset}
            totalItems={reviewRequestsResponse.pagination.count}
          />
        </>
      ) : null}
    </>
  );
}

export default ReviewRequests;
